import { Phone, Mail, Clock, ShieldCheck } from 'lucide-react';
import { useMatter } from '../hooks/useMatter';

/**
 * Contact page (route still /messages for back-compat). Shows the case manager
 * plus MARA disclosure. Replaces the prior "in-portal messaging coming soon"
 * placeholder, which was a feature backlog masquerading as a page.
 */
export default function Contact() {
  const { caseManager } = useMatter();
  const firstName = caseManager?.given_names ?? null;
  const fullName = caseManager
    ? [caseManager.given_names, caseManager.family_name].filter(Boolean).join(' ').trim()
    : null;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold mb-1">Contact your case manager</h1>
        <p className="text-slate-500">
          Direct lines to {firstName ?? 'your case manager'}. We typically reply within 24 to 48
          business hours, Melbourne time.
        </p>
      </div>

      {caseManager ? (
        <div className="card p-6">
          <div className="flex items-center gap-4 pb-5 border-b border-slate-100">
            <div className="w-14 h-14 rounded-full bg-[#EBF7FD] text-[#1A8DC7] grid place-items-center font-bold text-lg shrink-0">
              {(caseManager.given_names ?? '?')[0]}
              {(caseManager.family_name ?? '?')[0]}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-[#0B2341] truncate">{fullName}</div>
              <div className="text-xs text-slate-500 capitalize">
                {caseManager.staff_role ?? 'Case manager'} · ADI Immigration
              </div>
            </div>
          </div>

          <div className="pt-5 space-y-3">
            {caseManager.email && (
              <a
                href={`mailto:${caseManager.email}`}
                className="flex items-center gap-3 text-sm text-slate-700 hover:text-[#2CABEA] transition-colors"
              >
                <Mail size={16} className="text-slate-400 shrink-0" />
                <span className="truncate">{caseManager.email}</span>
              </a>
            )}
            {caseManager.phone && (
              <a
                href={`tel:${caseManager.phone}`}
                className="flex items-center gap-3 text-sm text-slate-700 hover:text-[#2CABEA] transition-colors"
              >
                <Phone size={16} className="text-slate-400 shrink-0" />
                <span>{caseManager.phone}</span>
              </a>
            )}
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <Clock size={16} className="text-slate-400 shrink-0" />
              <span>Replies within 24 to 48 business hours (Melbourne time)</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="card p-6 text-sm text-slate-500">
          A case manager will be assigned to your matter shortly. In the meantime, reach the ADI
          team using the details below.
        </div>
      )}

      <div className="card p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-[#2CABEA] shrink-0 mt-0.5" />
          <div className="text-xs text-slate-600 leading-relaxed">
            <div className="font-semibold text-[#0B2341] mb-1">Registered migration agent</div>
            <div>
              Eli Bekker - MARN 1465541
              <br />
              ADI Immigration · A service of Partner Visa Australia Pty Ltd · ABN 97 670 826 892
              <br />
              General line: 1300 729 331 · adiimmigration.com.au
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
